import React, { useState } from "react";
import { Receipt, Fuel, FileText, BookOpen, Image, Eye, X } from "lucide-react";
import { Button, Card, Badge, Spinner } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job Records Panel — receipts / forms / dockets / fuel stops / photos aggregated for one job
const JobRecordsPanel = ({ job, data, loading, onRefresh, onBack }) => {
  const [section, setSection] = useState('receipts');
  const [previewUrl, setPreviewUrl] = useState(null);

  if (loading || !data) {
    return (
      <div className="space-y-4" data-testid="job-records-panel">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold uppercase">Records - {job?.title}</h3>
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        </div>
        <div className="flex justify-center py-12"><Spinner /></div>
      </div>
    );
  }

  const c = data.counts || {};
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU') : '-';
  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const statusBadge = (s) => s === 'completed' ? 'success' : s === 'in_progress' || s === 'in-progress' ? 'warning' : s === 'draft' ? 'info' : s === 'cancelled' ? 'error' : 'default';

  const sections = [
    { key: 'receipts',  label: `Receipts (${c.receipts || 0})`,   icon: Receipt },
    { key: 'fuel',      label: `Fuel Stops (${c.fuel_stops || 0})`, icon: Fuel },
    { key: 'forms',     label: `Forms (${c.forms || 0})`,         icon: FileText },
    { key: 'dockets',   label: `Dockets (${c.dockets || 0})`,     icon: BookOpen },
    { key: 'photos',    label: `Photos (${c.photos || 0})`,       icon: Image }
  ];

  return (
    <div className="space-y-4" data-testid="job-records-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold uppercase">Records — {job?.title}</h3>
          <p className="text-xs text-slate-500">All uploads, forms, dockets and receipts for this job</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onRefresh}>Refresh</Button>
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Card className="text-center p-2"><p className="text-xl font-bold">{c.receipts || 0}</p><p className="text-[10px] text-slate-500 uppercase font-bold">Receipts · {money(data.receipts_total)}</p></Card>
        <Card className="text-center p-2"><p className="text-xl font-bold text-amber-600">{c.fuel_stops || 0}</p><p className="text-[10px] text-slate-500 uppercase font-bold">Fuel Stops</p></Card>
        <Card className="text-center p-2"><p className="text-xl font-bold text-blue-600">{c.forms || 0}</p><p className="text-[10px] text-slate-500 uppercase font-bold">Forms</p></Card>
        <Card className="text-center p-2"><p className="text-xl font-bold text-purple-600">{c.dockets || 0}</p><p className="text-[10px] text-slate-500 uppercase font-bold">Dockets</p></Card>
        <Card className="text-center p-2"><p className="text-xl font-bold text-green-600">{c.photos || 0}</p><p className="text-[10px] text-slate-500 uppercase font-bold">Photos</p></Card>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {sections.map(s => {
          const Icon = s.icon;
          const active = section === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              data-testid={`job-records-${s.key}-tab`}
              className={`px-3 py-2 font-bold text-xs uppercase -mb-[2px] border-b-2 flex items-center gap-2 whitespace-nowrap ${active ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {section === 'receipts' && (
        data.receipts.length === 0 ? (
          <Card className="text-center py-8 text-slate-500 text-sm">No receipts logged for this job yet</Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-lg border">
              <thead className="bg-slate-50"><tr>
                <th className="text-left p-2 font-bold">Date</th>
                <th className="text-left p-2 font-bold">Source</th>
                <th className="text-left p-2 font-bold">Type</th>
                <th className="text-right p-2 font-bold">Amount</th>
                <th className="text-left p-2 font-bold">By</th>
                <th className="text-left p-2 font-bold">Description</th>
                <th className="p-2"></th>
              </tr></thead>
              <tbody>
                {data.receipts.map(r => (
                  <tr key={`${r.source}-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2 text-xs">{fmt(r.date)}</td>
                    <td className="p-2"><Badge variant={r.source === 'fuel' ? 'warning' : r.source === 'daybook' ? 'warning' : 'info'}>{r.source}</Badge></td>
                    <td className="p-2 uppercase text-xs">{r.expense_type}</td>
                    <td className="p-2 text-right font-bold text-green-600">{money(r.amount)}</td>
                    <td className="p-2 text-xs">{r.user_name}</td>
                    <td className="p-2 text-xs text-slate-600 max-w-[220px] truncate">{r.description || '-'}</td>
                    <td className="p-2">
                      {r.receipt_url ? (
                        <Button size="sm" variant="ghost" onClick={() => setPreviewUrl({ url: r.receipt_url, caption: r.description })}><Eye className="w-3.5 h-3.5" /></Button>
                      ) : (
                        <span className="text-[10px] text-amber-600 font-semibold">NO PHOTO</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {section === 'fuel' && (
        data.fuel_stops.length === 0 ? (
          <Card className="text-center py-8 text-slate-500 text-sm">No fuel stops logged for trips to this job</Card>
        ) : (
          <div className="space-y-2">
            {data.fuel_stops.map(f => (
              <Card key={f.id} className="p-3 flex items-start gap-3">
                <div className="w-16 h-16 rounded bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {f.receipt_url ? (
                    <button onClick={() => setPreviewUrl({ url: f.receipt_url, caption: `${f.fuel_amount_l}L @ ${f.station}` })} className="w-full h-full">
                      <img src={f.receipt_url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <Fuel className="w-6 h-6 text-amber-500" />
                  )}
                </div>
                <div className="flex-1 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{f.fuel_amount_l}L @ {f.station || 'unknown'}</span>
                    <Badge variant="info">{f.payment_type || 'logged'}</Badge>
                    {!f.receipt_url && <Badge variant="warning">NO RECEIPT PHOTO</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {fmt(f.date)} · {f.trip} · {f.logged_by || 'Unknown'} · {f.km_at_stop ? `${f.km_at_stop}km` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">{money(f.fuel_cost)}</p>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {section === 'forms' && (
        data.forms.length === 0 ? (
          <Card className="text-center py-8 text-slate-500 text-sm">No forms submitted for this job yet</Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-lg border">
              <thead className="bg-slate-50"><tr>
                <th className="text-left p-2 font-bold">Submitted</th>
                <th className="text-left p-2 font-bold">Form</th>
                <th className="text-left p-2 font-bold">Category</th>
                <th className="text-left p-2 font-bold">By</th>
                <th className="text-left p-2 font-bold">Signatures</th>
                <th className="text-left p-2 font-bold">Approval</th>
              </tr></thead>
              <tbody>
                {data.forms.map(f => (
                  <tr key={f.id} className="border-t border-slate-100">
                    <td className="p-2 text-xs">{f.submitted_at ? new Date(f.submitted_at).toLocaleDateString('en-AU') : '-'}</td>
                    <td className="p-2 font-semibold">{f.template_name}</td>
                    <td className="p-2 text-xs uppercase">{f.template_category || '-'}</td>
                    <td className="p-2 text-xs">{f.user_name}</td>
                    <td className="p-2"><Badge variant={f.signature_status === 'complete' ? 'success' : 'warning'}>{f.signature_status || 'n/a'}</Badge></td>
                    <td className="p-2"><Badge variant={f.approval_status === 'approved' ? 'success' : f.approval_status === 'pending' ? 'warning' : 'default'}>{f.approval_status || 'n/a'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {section === 'dockets' && (
        data.dockets.length === 0 ? (
          <Card className="text-center py-8 text-slate-500 text-sm">No dockets for this job yet</Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-lg border">
              <thead className="bg-slate-50"><tr>
                <th className="text-left p-2 font-bold">Date</th>
                <th className="text-left p-2 font-bold">Status</th>
                <th className="text-left p-2 font-bold">Description</th>
                <th className="text-left p-2 font-bold">Labour/Equip</th>
                <th className="text-left p-2 font-bold">Client Signed</th>
                <th className="p-2"></th>
              </tr></thead>
              <tbody>
                {data.dockets.map(d => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="p-2 text-xs">{fmt(d.work_date)}</td>
                    <td className="p-2"><Badge variant={statusBadge(d.status)}>{(d.status || '').replace('_', ' ')}</Badge></td>
                    <td className="p-2 text-xs max-w-[260px] truncate">{d.description || d.work_type || '-'}</td>
                    <td className="p-2 text-xs">{(d.labour_entries?.length || 0)} / {(d.equipment_entries?.length || 0)}</td>
                    <td className="p-2 text-xs">{d.client_signature?.name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-2">
                      {d.status === 'completed' ? (
                        <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/daybook/${d.id}/pdf`, '_blank')}>
                          <Eye className="w-3.5 h-3.5 mr-1" />PDF
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-400">Pending sign-off</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {section === 'photos' && (
        data.machine_photos.length === 0 ? (
          <Card className="text-center py-8 text-slate-500 text-sm">No machine photos on this job yet</Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {data.machine_photos.map(p => (
              <button key={p.id} onClick={() => setPreviewUrl({ url: p.photo_url, caption: p.description })} className="rounded-lg border border-slate-200 overflow-hidden hover:border-orange-400 bg-slate-50 text-left">
                <img src={p.photo_url} alt="" className="w-full h-32 object-cover" />
                <div className="p-2 text-xs">
                  <div className="font-bold truncate">{p.machine_name || 'Machine'}</div>
                  <div className="text-slate-500 truncate">{p.description || p.source}</div>
                  <div className="text-slate-400 text-[10px]">{fmt(p.date)}</div>
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {/* Simple image preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="bg-white rounded-lg p-4 max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-bold text-sm">{previewUrl.caption || 'Receipt'}</p>
              <button onClick={() => setPreviewUrl(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <img src={previewUrl.url} alt="" className="w-full max-h-[75vh] object-contain rounded border" />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobRecordsPanel;
